<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SHARE_KEY_PARAM, shareKeyFromInput } from '@/utils/savedViewSharing';
import { useMountedWhileOpen } from '@/composables/useMountedWhileOpen';

/**
 * "Import a view": paste a share link someone sent you. It goes through the same
 * import page a clicked link opens, which shows the view and asks first.
 */

const open = defineModel<boolean>('open', { required: true });
const router = useRouter();

// In the page only while open or fading out, so a closed dialog leaves no Teleport behind.
const { mounted: dialogMounted, onCloseFinished: onDialogClosed } = useMountedWhileOpen(open);

const pasted = ref('');
const shareKey = computed(() => shareKeyFromInput(pasted.value));

watch(open, (isOpen) => {
    if (isOpen) {
        pasted.value = '';
    }
});

function openLink() {
    if (!shareKey.value) {
        return;
    }
    open.value = false;
    router.push({ name: 'import-saved-view', query: { [SHARE_KEY_PARAM]: shareKey.value } });
}
</script>

<template>
  <Dialog v-if="dialogMounted" v-model:open="open">
    <DialogContent class="sm:max-w-md" @close-auto-focus="onDialogClosed">
      <DialogHeader>
        <DialogTitle>Import a view</DialogTitle>
        <DialogDescription>Paste a share link someone sent you.</DialogDescription>
      </DialogHeader>
      <form class="flex flex-col gap-2" @submit.prevent="openLink">
        <label for="import-view-link" class="text-sm font-medium">Share link</label>
        <Input id="import-view-link" v-model="pasted" data-testid="import-view-link" autocomplete="off" autofocus />
        <p v-if="pasted.trim() !== '' && !shareKey" class="text-sm text-destructive-foreground" role="alert">
          That doesn't look like a share link.
        </p>
      </form>
      <DialogFooter>
        <Button variant="outline" @click="open = false">Cancel</Button>
        <Button :disabled="!shareKey" data-testid="import-view-open" @click="openLink">Open</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
